import { getApiBaseUrl } from '../config/api.config'
import {
  getActiveOrdersMockPreset,
  loadActiveOrdersMock,
} from '../mocks/activeOrdersMock'
import { getTokenFromUrl } from '../utils/auth'

import type { EnvironmentWithChina } from '../App'

/**
 * Active orders — Segment Profile API custom trait `active_orders`.
 *
 * GET /public/profileapi/{CustomerNumber}/active-orders
 * Downstream: Segment Profile API (https://www.twilio.com/docs/segment/unify/profile-api)
 * rETL data contract: https://suitsupply.atlassian.net/wiki/spaces/RP/pages/6517653563/Data+Contract
 * V2 (returns & exchanges): https://suitsupply.atlassian.net/wiki/spaces/RP/pages/6534922289/Data+contract+V2+inc+exchanges
 */

/** Shipping route for the card. */
export type ActiveOrderDeliveryMethod =
  | 'ShipToAddress'
  | 'ShipToStore'
  | 'StoreSale'
  | 'PickUpAtStore'

/**
 * MAO return type — the V2 discriminant for what kind of row this is.
 *
 * `null` is a regular order; `Refund` a return; either exchange value an
 * exchange. There is deliberately no `card_type` field: the producer expects
 * the frontend to derive the row type (and the image badge) from this alone,
 * because `fulfillment_status` reuses `On Its Way`/`Delivered` for both a
 * regular order and an exchange's outbound leg.
 */
export type ActiveOrderReturnType =
  | 'Refund'
  | 'Even Exchange'
  | 'Uneven Exchange'
  // Derived on a Return Received row from the money direction (RP-6194):
  // what the customer is owed, not what MAO recorded as requested.
  | 'Exchange'
  | 'Exchange with refund'
  | 'Exchange with payment'

/** Card status as produced by the rETL trait (title-cased, e.g. `Preparing Order`). */
export type ActiveOrderFulfillmentStatus =
  | 'Order Confirmed'
  | 'Preparing Order'
  | 'In Production'
  | 'On Its Way'
  | 'Ready for Pickup'
  | 'Alterations in Progress'
  | 'Delivered'
  | 'Purchased'
  | 'Picked-up'
  // V2 return/exchange states. `On Its Way` and `Delivered` above are reused
  // as-is for an exchange's outbound leg.
  | 'Return Registered'
  | 'Return Received'
  | 'Processing Return'
  | 'Processing Exchange'
  | 'Exchange Confirmed'

/**
 * `Next door` is required by RP-5243/5244 ("Delivered next door") but is not
 * yet in the published rETL contract — the producer needs to add it before
 * that card state can appear.
 */
export type ActiveOrderDeliveryLocation =
  | 'Home'
  | 'Store'
  | 'Pick-up point'
  | 'Next door'

export type ActiveOrderItemType = 'RTW' | 'Custom Made' | 'Custom Made Lite'

export interface ActiveOrderProduct {
  image_url: string | null
  /** Producer-derived from product_production_type + is_custom_made_lite. */
  item_type: ActiveOrderItemType | (string & {})
}

/**
 * One entry is one *card*, not one order: `order_id` is a foreign key to the
 * parent order and repeats when an order produces several cards (e.g. a
 * shipment card plus an alteration card).
 *
 * On a return/exchange row `order_id` is polymorphic — it carries the MAO
 * *return* order id (`order_type_id = Return`), not the original purchase.
 * A return order is always homogenous in type, so a mixed request (some items
 * refunded, some exchanged) arrives as two rows, never one.
 */
export interface ActiveOrder {
  order_id: string
  /** V2. Null on a regular order. Sole discriminant for the row type. */
  return_type?: ActiveOrderReturnType | (string & {}) | null
  delivery_method: ActiveOrderDeliveryMethod | (string & {})
  fulfillment_status: ActiveOrderFulfillmentStatus | (string & {})
  /** ISO 8601 (YYYY-MM-DD). Null until allocated; always null on alteration cards. */
  delivery_promise: string | null
  /** ISO 8601 (YYYY-MM-DD). Null until delivered. */
  delivered_on_date: string | null
  /**
   * Null until the shipment is dispatched (DWH `has_tracking` flag).
   *
   * Polymorphic on a return/exchange row: inbound tracking while the garment
   * heads back, flipping to the replacement's outbound tracking once that leg
   * ships — whichever matches the current `fulfillment_status`.
   */
  order_tracking_link: string | null
  /**
   * V2. ISO 8601 (YYYY-MM-DD). When the return/exchange was registered in MAO;
   * null on a regular order.
   */
  registered_on_date?: string | null
  /**
   * V2. QR/label payload behind "Show return label". Only populated while
   * `fulfillment_status` is `Return Registered`.
   */
  return_label_url?: string | null
  /**
   * Client-side only, never sent by the API. Set with a `return_label_url`
   * that `useReturnLabels` resolved from storage, so the CTA downloads the
   * file under its stored name.
   */
  return_label_download_name?: string | null
  delivery_location: ActiveOrderDeliveryLocation | (string & {})
  /** ISO 8601 (YYYY-MM-DD). Alteration cards only; set once the alteration starts. */
  alteration_delivery_promise: string | null
  /** Alteration cards and ship-to-store cards only, otherwise null. */
  store_code: string | null
  /** Alteration cards and ship-to-store cards only, otherwise null. */
  store_name: string | null
  /** Two-letter country code, e.g. `nl`. */
  store_country_code: string | null
  /** One entry per garment on this card. */
  products: ActiveOrderProduct[]
}

export class ActiveOrdersApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: unknown,
  ) {
    super(message)
    this.name = 'ActiveOrdersApiError'
  }
}

export function getActiveOrdersUrl(
  customerNumber: string,
  env: EnvironmentWithChina,
): string {
  return `${getApiBaseUrl(env)}/public/profileapi/${encodeURIComponent(
    customerNumber,
  )}/active-orders`
}

export async function fetchActiveOrders(
  customerNumber: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<ActiveOrder[]> {
  const normalizedCustomerNumber = customerNumber.trim()

  if (!normalizedCustomerNumber) {
    throw new ActiveOrdersApiError('Missing customer number.')
  }

  // Local fixtures instead of the gateway — off by default, never on `prd`.
  // Checked before the token, so the mock works without one.
  const mockPreset = getActiveOrdersMockPreset(env)
  if (mockPreset) {
    const orders = await loadActiveOrdersMock(mockPreset)
    // Unconditional, unlike `debug`: seeing this line is how you confirm the
    // flag took effect, and it is unreachable on `prd`.
    console.info(
      `[ActiveOrders] Mock enabled (preset "${mockPreset}") — serving ${orders.length} fixture rows instead of the API.`,
    )

    return orders
  }

  const url = getActiveOrdersUrl(normalizedCustomerNumber, env)
  const token = authToken || getTokenFromUrl()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'omit',
    })

    if (!response.ok) {
      const errorBody: unknown = await response
        .json()
        .catch(() => response.text().catch(() => null))
      throw new ActiveOrdersApiError(
        `Failed to fetch active orders: ${response.statusText}`,
        response.status,
        errorBody,
      )
    }

    const data: unknown = await response.json()
    return Array.isArray(data) ? (data as ActiveOrder[]) : []
  } catch (error) {
    if (error instanceof ActiveOrdersApiError) {
      throw error
    }

    throw new ActiveOrdersApiError(
      `Network error while fetching active orders: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
    )
  }
}
